import { CARD_ORDER, playsOfType } from '../sim/plays';
import { buildRoute } from '../sim/routes';
import type { Play, PlayType } from '../sim/types';
import { el, onTap } from './dom';

const TYPE_LABEL: Record<PlayType, string> = {
  run: 'RUN',
  short: 'SHORT PASS',
  deep: 'DEEP PASS',
  playaction: 'PLAY ACTION',
};

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Mini play diagram drawn from the play data: formation dots and route lines, downfield is up. */
export function playDiagram(play: Play): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '-20 -24 40 31');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('aria-hidden', 'true');
  const los = document.createElementNS(SVG_NS, 'line');
  los.setAttribute('x1', '-20');
  los.setAttribute('x2', '20');
  los.setAttribute('y1', '0');
  los.setAttribute('y2', '0');
  los.setAttribute('stroke', 'currentColor');
  los.setAttribute('stroke-width', '0.3');
  los.setAttribute('opacity', '0.4');
  svg.appendChild(los);
  for (const r of play.routes) {
    const slot = play.formation.slots[r.slot];
    if (!slot) continue;
    const route = buildRoute(r.route, slot, false, r.depth);
    const line = document.createElementNS(SVG_NS, 'polyline');
    // Clip very deep routes so the card stays readable.
    line.setAttribute('points', route.points.map((p) => `${p.x},${-Math.min(p.y, 22)}`).join(' '));
    line.setAttribute('fill', 'none');
    line.setAttribute('stroke', 'currentColor');
    line.setAttribute('stroke-width', r.slot === play.primaryRead ? '1' : '0.6');
    line.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(line);
  }
  for (const s of play.formation.slots) {
    const dot = document.createElementNS(SVG_NS, 'circle');
    dot.setAttribute('cx', String(s.x));
    dot.setAttribute('cy', String(-s.y));
    dot.setAttribute('r', '0.9');
    dot.setAttribute('fill', 'currentColor');
    svg.appendChild(dot);
  }
  return svg;
}

export interface PlayCallHandlers {
  onPick(play: Play): void;
  onSnap(): void;
  onFlip(): void;
}

/** Four cards across the bottom. First tap picks the card, a second tap cycles its two plays, SNAP starts the play. */
export class PlayCallView {
  readonly root = el('div', 'playcall');
  private readonly clock = el('div', 'playcall-clock panel');
  private readonly snapBtn = el('button', 'btn btn-primary', 'SNAP');
  private readonly flipBtn = el('button', 'btn btn-ghost', 'FLIP');
  private readonly cards = new Map<PlayType, HTMLButtonElement>();
  private readonly cycle = new Map<PlayType, number>();
  private selected: Play | null = null;

  constructor(private readonly h: PlayCallHandlers) {
    const top = el('div', 'playcall-top');
    top.append(el('div', 'playcall-title panel', 'PLAY CALL'), this.clock, el('div', 'playcall-spacer'), this.flipBtn, this.snapBtn);
    this.snapBtn.dataset.testid = 'snap';
    this.flipBtn.dataset.testid = 'flip';
    onTap(this.snapBtn, () => this.h.onSnap());
    onTap(this.flipBtn, () => this.h.onFlip());

    const row = el('div', 'cards');
    for (const type of CARD_ORDER) {
      const card = el('button', 'card');
      card.dataset.type = type;
      card.dataset.testid = `card-${type}`;
      const plays = playsOfType(type);
      card.disabled = !plays.some((p) => p.playable);
      onTap(card, () => this.tapCard(type));
      this.cards.set(type, card);
      this.cycle.set(type, 0);
      row.appendChild(card);
    }
    this.root.append(top, row);
    this.renderCards();
  }

  private tapCard(type: PlayType): void {
    const plays = playsOfType(type).filter((p) => p.playable);
    if (plays.length === 0) return;
    let idx = this.cycle.get(type) ?? 0;
    if (this.selected?.type === type) idx = (idx + 1) % plays.length;
    this.cycle.set(type, idx);
    const play = plays[idx]!;
    this.selected = play;
    this.renderCards();
    this.h.onPick(play);
  }

  /** Picks a card for the player (play clock ran out). */
  autoPick(type: PlayType): void {
    this.tapCardOnce(type);
  }

  private tapCardOnce(type: PlayType): void {
    if (this.selected?.type === type) return;
    this.tapCard(type);
  }

  private renderCards(): void {
    for (const type of CARD_ORDER) {
      const card = this.cards.get(type)!;
      const plays = playsOfType(type);
      const play = plays[this.cycle.get(type) ?? 0] ?? plays[0]!;
      card.replaceChildren(
        el('div', 'card-name', play.name),
        el('div', 'card-type', card.disabled ? `${TYPE_LABEL[type]}: SOON` : TYPE_LABEL[type]),
        el('div', 'card-cycle', plays.length > 1 && !card.disabled ? `${(this.cycle.get(type) ?? 0) + 1}/${plays.length}` : ''),
        playDiagram(play),
      );
      card.classList.toggle('selected', this.selected?.id === play.id);
    }
    this.snapBtn.hidden = this.selected === null;
  }

  show(suggested: PlayType): void {
    this.selected = null;
    for (const [type, card] of this.cards) card.classList.toggle('suggested', type === suggested);
    this.renderCards();
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }

  setClock(seconds: number): void {
    this.clock.textContent = String(Math.max(0, Math.ceil(seconds)));
  }

  setAutoSnap(seconds: number | null): void {
    this.snapBtn.textContent = seconds === null ? 'SNAP' : `SNAP ${Math.max(0, Math.ceil(seconds))}`;
  }
}
